import {
  Button,
  Group,
  Icon,
  IconButton,
  Stack,
  Text,
  Thumbnail,
} from "@assetwatch/ui";

export type ProcedurePhotoTileStatus =
  | { kind: "saved" }
  | { kind: "uploading" }
  | { kind: "saving" }
  | { kind: "removing" }
  | {
      kind: "failed";
      message: string;
      retryLabel: "Retry upload" | "Retry save" | "Upload again" | null;
    };

function StatusOverlay({ status }: { status: ProcedurePhotoTileStatus }) {
  if (
    status.kind !== "uploading" &&
    status.kind !== "saving" &&
    status.kind !== "failed"
  )
    return null;
  return (
    /* @ds-deviation: Thumbnail has no status slot, so the pending spinner and failed warning are layered over it with a token scrim — promote a Thumbnail status overlay (pending/failed) into the design system */
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-(--radius-sm) bg-surface-scrim text-text-inverse"
      data-slot="procedure-photo-status"
    >
      {status.kind === "failed" ? (
        <Icon name="warning" size="sm" tone="warning" />
      ) : (
        <span className="aw-spinner inline-flex">
          <Icon name="button-load-spinner" size="sm" tone="inherit" />
        </span>
      )}
    </div>
  );
}

/**
 * One photo on a Procedure PHOTO item: a DS Thumbnail with its file name, a
 * remove action and, while it is not saved yet, its upload state.
 */
export function ProcedurePhotoTile({
  alt,
  fileName,
  onOpen,
  onRemove,
  onRetry,
  src,
  status,
}: {
  alt: string;
  fileName: string;
  src: string;
  status: ProcedurePhotoTileStatus;
  onOpen?: () => void;
  onRemove?: () => void;
  onRetry?: () => void;
}) {
  return (
    <div
      className="flex w-(--spacing-128) flex-col gap-(--spacing-4)"
      data-testid="procedure-photo-tile"
    >
      <div className="relative inline-flex self-start">
        {onOpen && src ? (
          <Thumbnail
            alt={alt}
            aria-label={`Open ${fileName}`}
            onClick={onOpen}
            src={src}
          />
        ) : src ? (
          <Thumbnail alt={alt} src={src} />
        ) : (
          <Thumbnail aria-label={alt} />
        )}
        <StatusOverlay status={status} />
      </div>
      {/* @ds-deviation: Thumbnail has no caption or remove slot, so the file name and remove action are composed beside it — promote a removable captioned Thumbnail into the design system */}
      <Group gap={4} justify="between" wrap="nowrap">
        <span className="min-w-0 flex-1">
          <Text size="sm" title={fileName} truncate>
            {fileName}
          </Text>
        </span>
        {onRemove ? (
          <IconButton
            aria-label={`Remove ${fileName}`}
            icon="x-close"
            loading={status.kind === "removing"}
            onClick={onRemove}
            size="sm"
            variant="ghost"
          />
        ) : null}
      </Group>
      {status.kind === "uploading" || status.kind === "saving" ? (
        <Text role="status" size="sm" tone="subtle">
          {status.kind === "uploading" ? "Uploading" : "Saving"}
        </Text>
      ) : null}
      {status.kind === "failed" ? (
        <Stack gap={4}>
          <Text role="alert" size="sm" tone="negative">
            {status.message}
          </Text>
          {status.retryLabel && onRetry ? (
            <span className="self-start">
              <Button onClick={onRetry} size="sm" variant="ghost">
                {status.retryLabel}
              </Button>
            </span>
          ) : null}
        </Stack>
      ) : null}
    </div>
  );
}
